import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Send } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg } from "@/lib/api";
import { useMarket } from "@/lib/market";
import { PREVIEWS } from "@/components/public/Previews";

export const IMAGES = {};

export const media = (u) => (u && u.startsWith("/api/") ? `${API}${u.slice(4)}` : u);

export function useSitePage(page) {
  const [market] = useMarket();
  const [blocks, setBlocks] = useState(null);
  useEffect(() => {
    api.get(`/site/page/${page}`, { params: { market } }).then(({ data }) => setBlocks(data)).catch(() => setBlocks([]));
  }, [page, market]);
  return blocks;
}

const vis = (b) => (b.variant === "mobile" ? "md:hidden" : b.variant === "desktop" ? "hidden md:block" : "");

export function Cta({ label, url, ghost, testId }) {
  if (!label || !url) return null;
  const cls = ghost ? "p-btn-ghost" : "p-btn";
  const inner = <>{label} {!ghost && <ArrowRight size={16} />}</>;
  if (url.startsWith("#") || url.startsWith("http") || url.startsWith("mailto:")) return <a href={url} className={cls} data-testid={testId}>{inner}</a>;
  return <Link to={url} className={cls} data-testid={testId}>{inner}</Link>;
}

export function Hero({ block, testId = "pub-hero", children, aside }) {
  if (!block) return <div className="h-[50vh]" />;
  const photo = media(block.media_url);
  return (
    <section className={`${photo ? "p-photo" : "border-b p-line"} ${vis(block)}`} style={photo ? { backgroundImage: `url(${photo})` } : undefined} data-testid={testId}>
      <div className={`mx-auto max-w-7xl px-5 pb-16 pt-14 md:pb-24 md:pt-20 ${aside ? "grid items-center gap-12 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] [&>*]:min-w-0" : ""}`}>
      <div>
        {block.eyebrow && <p className="p-eyebrow mpl-rise">{block.eyebrow}</p>}
        <h1 className={`mpl-rise mt-4 max-w-5xl font-display text-5xl uppercase leading-[0.9] [overflow-wrap:break-word] ${aside ? "sm:text-6xl lg:text-7xl" : "sm:text-7xl lg:text-8xl"}`} lang="fr" style={{ animationDelay: "80ms" }} data-testid={`${testId}-title`}>{block.title}</h1>
        {(block.subtitle || block.body) && <p className={`mpl-rise mt-6 max-w-2xl text-lg md:text-xl ${photo ? "p-on-photo-2" : "p-t2"}`} style={{ animationDelay: "160ms" }}>{block.subtitle || block.body}</p>}
        {block.bullets?.length > 0 && <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2">{block.bullets.map((t) => <li key={t} className="flex items-center gap-1.5 text-sm font-bold"><Check size={15} className="p-accent" /> {t}</li>)}</ul>}
        <div className="mpl-rise mt-8 flex flex-wrap gap-3" style={{ animationDelay: "240ms" }}>
          <Cta label={block.cta_label} url={block.cta_url} testId={`${testId}-cta`} />
          <Cta label={block.cta2_label} url={block.cta2_url} ghost testId={`${testId}-cta2`} />
        </div>
        {children}
      </div>
      {aside}
      </div>
    </section>
  );
}

export function Section({ block, aside, index, flip, testId }) {
  const photo = media(block.media_url);
  const side = photo ? <img src={photo} alt={block.title} loading="lazy" className="aspect-[4/3] w-full rounded-sm object-cover" /> : aside;
  return (
    <section className={`mx-auto grid items-center gap-10 px-5 py-14 md:py-20 ${side ? "max-w-7xl md:grid-cols-2" : "max-w-4xl"} ${vis(block)}`} data-testid={testId}>
      <div className={flip ? "md:order-2" : ""}>
        <div className="flex items-baseline gap-4">
          {index != null && <span className="p-num text-6xl p-accent">{String(index).padStart(2, "0")}</span>}
          {block.eyebrow && <p className="p-eyebrow">{block.eyebrow}</p>}
        </div>
        <h2 className="mt-3 text-4xl font-bold uppercase leading-[0.95] sm:text-5xl">{block.title}</h2>
        {block.subtitle && <p className="mt-3 text-lg font-semibold p-t2">{block.subtitle}</p>}
        {block.body && <p className="mt-4 max-w-xl text-base p-t2">{block.body}</p>}
        {block.bullets?.length > 0 && <ul className="mt-6 grid gap-2 sm:grid-cols-2">{block.bullets.map((t) => <li key={t} className="flex items-start gap-2 text-sm p-t2"><Check size={16} className="mt-0.5 shrink-0 p-accent" /> {t}</li>)}</ul>}
        <div className="mt-8 flex flex-wrap gap-3"><Cta label={block.cta_label} url={block.cta_url} testId={`${testId}-cta`} /><Cta label={block.cta2_label} url={block.cta2_url} ghost testId={`${testId}-cta2`} /></div>
      </div>
      {side && <div className={flip ? "md:order-1" : ""}>{side}</div>}
    </section>
  );
}

export function Examples({ universe, title = "Aperçus" }) {
  const [market] = useMarket();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/site/examples", { params: { universe, market } }).then(({ data }) => setRows(data)).catch(() => setRows([])); }, [universe, market]);
  if (!rows.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-5 py-16" data-testid={`pub-examples-${universe}`}>
      <p className="p-eyebrow">{title}</p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{rows.map((x) => {
        const P = PREVIEWS[x.preview];
        const body = (<>
          {x.image_url ? <img src={media(x.image_url)} alt="" loading="lazy" className="aspect-[16/10] w-full object-cover" /> : P && <div className="p-4 pb-0"><P /></div>}
          <div className="p-5"><div className="flex gap-2">{x.is_demo && <span className="p-chip">{P ? "Aperçu illustratif" : "Démo"}</span>}{x.market !== "ALL" && <span className="p-chip">{x.market === "FR" ? "France" : "Canada"}</span>}</div>
            <h3 className="mt-2 text-lg font-bold">{x.title}</h3>{x.text && <p className="mt-1 text-sm p-t3">{x.text}</p>}</div></>);
        return x.link_url ? <a key={x.id} href={x.link_url} className="p-surface block overflow-hidden transition-transform hover:-translate-y-1" data-testid={`pub-example-${x.id}`}>{body}</a>
          : <div key={x.id} className="p-surface overflow-hidden" data-testid={`pub-example-${x.id}`}>{body}</div>;
      })}</div>
    </section>
  );
}

const EMPTY = { company: "", name: "", email: "", country: "FR", city: "", message: "", website: "" };

export function ContactForm({ universe, title }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/public/contact", { ...f, universe, city: f.city || null, website: f.website || null }); setSent(true); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <section id="contact" className="mx-auto max-w-3xl scroll-mt-24 px-5 py-16" data-testid={`pub-contact-${universe}`}>
      <p className="p-eyebrow">Parlons-en</p>
      <h2 className="mt-2 font-display text-4xl font-extrabold uppercase">{title}</h2>
      {sent ? <p className="p-surface mt-6 p-6 text-base" data-testid="pub-contact-sent">Merci ! Ta demande est bien reçue, on te répond rapidement.</p> : (
        <form onSubmit={submit} className="mt-6 grid gap-3 sm:grid-cols-2">
          <input className="p-input" required minLength={2} placeholder="Organisation" value={f.company} onChange={set("company")} aria-label="Organisation" data-testid="pub-contact-company" />
          <input className="p-input" required minLength={2} placeholder="Ton nom" value={f.name} onChange={set("name")} aria-label="Nom" data-testid="pub-contact-name" />
          <input className="p-input" required type="email" placeholder="Courriel" value={f.email} onChange={set("email")} aria-label="Courriel" data-testid="pub-contact-email" />
          <div className="flex gap-2">
            <select className="p-input" value={f.country} onChange={set("country")} aria-label="Pays" data-testid="pub-contact-country"><option value="FR">France</option><option value="CA">Canada</option></select>
            <input className="p-input" placeholder="Ville" value={f.city} onChange={set("city")} aria-label="Ville" data-testid="pub-contact-city" />
          </div>
          <textarea className="p-input sm:col-span-2" required minLength={5} rows={4} placeholder="Ton projet en quelques lignes" value={f.message} onChange={set("message")} aria-label="Message" data-testid="pub-contact-message" />
          <input className="hidden" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} aria-hidden="true" />
          <button disabled={busy} className="p-btn justify-self-start" data-testid="pub-contact-submit"><Send size={15} /> Envoyer</button>
        </form>)}
    </section>
  );
}
